import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import useScrollToTop from '../../hooks/useScrollToTop';
import { 
  useCourseApi, 
  CourseDetails, 
  Lesson,
  VideoLessonResponse,
  QuizLessonResponse,
  FileLessonResponse,
  AssignmentLessonResponse,
} from '../../hooks/useCourseApi';
import ReactPlayer from 'react-player';
import { QuizContent } from '../quiz/QuizContent';
import { AssignmentContent } from '../assignment/AssignmentContent';
import { AssignmentForStudent } from '../../types/assignment';
import axios from 'axios';
import { BASE_URL } from '../../apiConfig';
import VideoWatermark from '../common/VideoWatermark';
import { getUserPhoneNumber } from '../../utils/userWatermark';
import UnitAccessTimer from '../common/UnitAccessTimer';
import { getTenantHeaders } from '../../config/tenant';
import QualitySelector from '../video/QualitySelector';
import PlyrYouTubePlayer, { PlyrYouTubePlayerHandle } from '../video/PlyrYouTubePlayer';
import { useVideoQuality } from '../../hooks/useVideoQuality';
import { isYouTubeUrl, isBunnyVideo } from '../../utils/videoQuality';
import {
  applyYoutubeDoubleClickSeek,
  YOUTUBE_OVERLAY_SINGLE_CLICK_MS,
} from '../../utils/youtubeDoubleClickSeek';
import { mapCourseLoadError } from '../../utils/courseLoadLabels';
import { cardTheme } from '../../styles/cardTheme';

// Add these color variables at the top of the file
const colors = {
  primary: '#2563EB', // Blue
  secondary: '#4F46E5', // Indigo
  accent: '#0EA5E9', // Sky blue
  success: '#22C55E', // Green
  background: '#F8FAFC', // Light gray
  text: {
    primary: '#1E293B', // Slate 800
    secondary: '#64748B', // Slate 500
    light: '#94A3B8' // Slate 400
  }
};

// Simplified screen orientation type
interface ScreenOrientationAPI {
  lock(orientation: 'landscape' | 'portrait'): Promise<void>;
  unlock(): void;
}

type SeekablePlayerRef =
  | PlyrYouTubePlayerHandle
  | (ReactPlayer & {
      seekTo: (amount: number, type?: 'seconds' | 'fraction') => void;
      getCurrentTime: () => number;
      getDuration: () => number;
    });

interface BunnyVideoPlayerProps {
  url: string;
  playing: boolean;
  onProgress: (state: { played: number; playedSeconds: number; loaded: number; loadedSeconds: number }) => void;
  onDuration: (duration: number) => void;
}

// Bunny Video Player Component
const BunnyVideoPlayer: React.FC<BunnyVideoPlayerProps> = ({ url, playing, onProgress, onDuration }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const playerInterval = useRef<number | null>(null);
  
  // Setup message listener for communication with iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only handle messages from our iframe
      if (iframeRef.current && event.source === iframeRef.current.contentWindow) {
        const data = event.data;
        
        // Handle duration info
        if (data.duration) {
          onDuration(data.duration);
        }
        
        // Handle progress updates
        if (data.currentTime && data.duration) {
          onProgress({ 
            played: data.currentTime / data.duration,
            playedSeconds: data.currentTime,
            loaded: 1, // Assume fully loaded
            loadedSeconds: data.duration
          });
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [onDuration, onProgress]);

  // Control player with play/pause
  useEffect(() => {
    if (isLoaded && iframeRef.current) {
      const iframe = iframeRef.current;
      try {
        const message = playing ? { action: 'play' } : { action: 'pause' };
        iframe.contentWindow?.postMessage(message, '*');
      } catch (error) {
        console.error('Error controlling Bunny player:', error);
      }
    }
  }, [playing, isLoaded]);

  // Fallback progress tracking when postMessage is not available
  useEffect(() => {
    if (isLoaded && playing) {
      // Clear any existing interval
      if (playerInterval.current) {
        clearInterval(playerInterval.current);
      }
      
      // Set up progress tracking
      playerInterval.current = window.setInterval(() => {
        if (iframeRef.current) {
          // Try to get current time via postMessage
          iframeRef.current.contentWindow?.postMessage({ action: 'getCurrentTime' }, '*');
        }
      }, 1000);
    } else if (playerInterval.current) {
      clearInterval(playerInterval.current);
    }
    
    return () => {
      if (playerInterval.current) {
        clearInterval(playerInterval.current);
      }
    };
  }, [playing, isLoaded]);

  return (
    <iframe
      ref={iframeRef}
      src={`${url}${url.includes('?') ? '&' : '?'}autoplay=false&loop=false&muted=false&preload=true&responsive=true`}
      className="w-full h-full border-0"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
      }}
      allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
      allowFullScreen
      title="Bunny Video Player"
      onLoad={() => setIsLoaded(true)}
    />
  );
};

// Add this CSS class for fullscreen video container
const videoContainerStyles = {
  fullscreen: {
    width: '100%',
    height: '100%',
    backgroundColor: '#000',
  }
};

// Add this helper function at the top of the file
const isArabicText = (text: string): boolean => {
  const arabicPattern = /[\u0600-\u06FF]/;
  return arabicPattern.test(text);
};

const isQuizContent = (x: any): x is QuizLessonResponse['data'] =>
  !!x &&
  typeof x === 'object' &&
  Array.isArray((x as any).questions) &&
  !('hasSubmitted' in x) &&
  ('isRetake' in x || 'passDegree' in x || 'isScoreDisplayed' in x);

const isAssignmentContent = (x: any): x is AssignmentForStudent =>
  !!x &&
  typeof x === 'object' &&
  'hasSubmitted' in x &&
  Array.isArray((x as any).questions);

// Add this helper to clear course details cache
const clearCourseDetailsCache = (courseId: string) => {
  try {
    // The cache key must match the one used in useCourseApi
    const params = { courseid: courseId };
    const key = '/api/Course/tree-course-with-progress?' + new URLSearchParams(params as any).toString();
    // Note: Cache clearing is handled internally by useCourseApi
    // This function is kept for potential future use
  } catch (e) { /* ignore */ }
};

// Add these helper functions at the top of the component, after the existing helper functions

// Helper function to check if a unit is accessible based on enrollment date
const isUnitAccessible = (unitIndex: number, enrollmentDate: string, isOpenToAll: boolean): { accessible: boolean; message?: string; daysLeft?: number } => {
  // If course is open to all, all units are accessible
  if (isOpenToAll) {
    return { accessible: true };
  }

  if (!enrollmentDate) {
    return { accessible: false, message: 'تاريخ التسجيل غير متوفر' };
  }

  const enrollment = new Date(enrollmentDate);
  const now = new Date();
  const daysSinceEnrollment = Math.floor((now.getTime() - enrollment.getTime()) / (1000 * 60 * 60 * 24));
  
  // All units start from enrollment date (day 0)
  // Each unit ends at different times:
  // First unit (index 0): ends after 7 days
  // Second unit (index 1): ends after 14 days
  // Third unit (index 2): ends after 21 days, etc.
  const unitStartDay = 0; // All units start from enrollment date
  const unitEndDay = (unitIndex + 1) * 7; // Each unit ends after (unitIndex + 1) * 7 days
  
  // Check if we're in the access period for this unit
  if (daysSinceEnrollment >= unitStartDay && daysSinceEnrollment < unitEndDay) {
    const daysLeft = unitEndDay - daysSinceEnrollment;
    return { accessible: true, daysLeft };
  }
  
  // Allow access to units even before their scheduled start date
  if (daysSinceEnrollment < unitStartDay) {
    return { accessible: true };
  }
  
  // Unit period has ended
  return { 
    accessible: false, 
    message: 'انتهت فترة الوصول لهذه الوحدة' 
  };
};

// Helper function to get unit access status for display
const getUnitAccessStatus = (unitIndex: number, enrollmentDate: string, isOpenToAll: boolean, courseData: CourseDetails | null) => {
  const status = isUnitAccessible(unitIndex, enrollmentDate, isOpenToAll);
  
  if (status.accessible && status.daysLeft !== undefined) {
    return {
      ...status,
      statusText: `متاحة (${status.daysLeft} ${status.daysLeft === 1 ? 'يوم متبقي' : 'أيام متبقية'})`,
      statusColor: status.daysLeft <= 2 ? 'text-orange-600' : 'text-green-600'
    };
  }
  
  if (status.accessible && isOpenToAll) {
    return {
      ...status,
      statusText: 'متاحة دائماً',
      statusColor: 'text-green-600'
    };
  }
  
  return {
    ...status,
    statusText: status.message || 'غير متاحة',
    statusColor: 'text-red-600'
  };
};

const CourseViewer: React.FC = () => {
  useScrollToTop();
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { fetchCourseDetails, isLoading, fetchLessonDetails } = useCourseApi();
  const [courseDetails, setCourseDetails] = useState<CourseDetails | null>(null);
  const [activeUnit, setActiveUnit] = useState<number | null>(null);
  const [activeLesson, setActiveLesson] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const playerRef = useRef<SeekablePlayerRef | null>(null);
  const [completedLessons, setCompletedLessons] = useState<string[]>([]);
  const [showCompleteButton, setShowCompleteButton] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [lessonDetails, setLessonDetails] = useState<
    | VideoLessonResponse['data']
    | QuizLessonResponse['data']
    | FileLessonResponse['data']
    | AssignmentLessonResponse['data']
    | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [isLandscape, setIsLandscape] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const controlsTimerRef = useRef<NodeJS.Timeout | null>(null);
  const youtubeOverlaySingleClickTimerRef = useRef<number | null>(null);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showBravo, setShowBravo] = useState(false);
  const [userPhoneNumber, setUserPhoneNumber] = useState<string | null>(null);
  const [qualityChangeNotification, setQualityChangeNotification] = useState<string | null>(null);
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(null);

  // Video quality management
  const { 
    selectedQuality, 
    connectionSpeed, 
    setQuality 
  } = useVideoQuality();

  const playbackSpeeds = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

  // Handle quality change with notification
  const handleQualityChange = (newQuality: string) => {
    const currentTime = playerRef.current?.getCurrentTime?.() || 0;
    setQuality(newQuality);
    
    // Show notification
    const qualityLabel = newQuality === 'auto' ? 'Auto' : 
                        newQuality === 'hd1080' ? '1080p' :
                        newQuality === 'hd720' ? '720p' :
                        newQuality === 'medium' ? '480p' :
                        newQuality === 'small' ? '360p' :
                        newQuality === 'tiny' ? '240p' : newQuality;
    
    const message = lessonDetails && isVideoLessonResponse(lessonDetails) && isYouTubeUrl(lessonDetails.videoUrl) 
      ? `Quality set to ${qualityLabel}. YouTube will adjust based on your connection.`
      : `Video quality changed to ${qualityLabel}`;
    
    setQualityChangeNotification(message);
    
    // Hide notification after 3 seconds
    setTimeout(() => {
      setQualityChangeNotification(null);
    }, 3000);

    // Restore playback position after player reloads
    setTimeout(() => {
      if (playerRef.current && currentTime > 0) {
        playerRef.current.seekTo(currentTime);
      }
    }, 1000);
  };

  // Function to show controls and set up auto-hide timer
  const showControlsTemporarily = () => {
    setControlsVisible(true);
    
    // Clear any existing timer
    if (controlsTimerRef.current) {
      clearTimeout(controlsTimerRef.current);
    }
    
    // Set up new timer to hide controls after 5 seconds
    controlsTimerRef.current = setTimeout(() => {
      // Only hide controls if video is playing
      if (playing) {
        setControlsVisible(false);
      }
    }, 5000);
  };

  // Reset controls visibility on play/pause
  useEffect(() => {
    if (playing) {
      showControlsTemporarily();
    } else {
      setControlsVisible(true);
      if (controlsTimerRef.current) {
        clearTimeout(controlsTimerRef.current);
      }
    }
  }, [playing]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (controlsTimerRef.current) {
        clearTimeout(controlsTimerRef.current);
      }
      if (youtubeOverlaySingleClickTimerRef.current) {
        clearTimeout(youtubeOverlaySingleClickTimerRef.current);
      }
    };
  }, []);

  // Get user phone number for watermark
  useEffect(() => {
    const phoneNumber = getUserPhoneNumber();
    setUserPhoneNumber(phoneNumber);
  }, []);

  const handleLessonSelect = async (unitIndex: number, lessonIndex: number) => {
    if (!courseDetails) {
      console.error('No course details available');
      return;
    }

    const unit = courseDetails.units[unitIndex];
    if (!unit || !unit.lessons || lessonIndex >= unit.lessons.length) {
      console.error('Invalid unit or lesson index:', { unitIndex, lessonIndex, unit });
      setError('الدرس غير متوفر');
      return;
    }

    const lesson = unit.lessons[lessonIndex];
    console.log('Selected lesson:', lesson);

    setPlaying(false);
    setLessonDetails(null);
    setSelectedLesson(lesson);
    setActiveUnit(unitIndex);
    setActiveLesson(lessonIndex);
    setLoading(true);
    setError(null);

    try {
      console.log('Fetching lesson details for lesson ID:', lesson.id);
      const response = await fetchLessonDetails(lesson.id);
      console.log('Lesson details response:', response);

      if (response?.success) {
        setLessonDetails(response.data);
      } else {
        console.error('Failed to load lesson details:', response);
        setError('فشل في تحميل تفاصيل الدرس');
      }
    } catch (err) {
      console.error('Error loading lesson:', err);
      setError('حدث خطأ أثناء تحميل الدرس');
    } finally {
      setLoading(false);
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('unit', String(unitIndex));
    nextParams.set('lesson', String(lessonIndex));
    nextParams.set('lessonId', String(lesson.id));
    setSearchParams(nextParams, { replace: true });
  };

  // Helper function to find and load the first available lesson
 const findAndLoadFirstLesson = async (courseData: CourseDetails) => {
  const allLessons: { unitIndex: number; lessonIndex: number; lesson: Lesson }[] = [];

  // Flatten all lessons with their indices, but only from accessible units
  courseData.units.forEach((unit, unitIndex) => {
    const unitAccess = isUnitAccessible(unitIndex, courseData.enrollmentDate, courseData.isOpenToAll);
    if (unitAccess.accessible) {
      unit.lessons.forEach((lesson, lessonIndex) => {
        allLessons.push({ unitIndex, lessonIndex, lesson });
      });
    }
  });

  // If no accessible lessons, send student to curriculum (units may exist with empty lessons)
  if (allLessons.length === 0) {
    const totalLessons = courseData.units.reduce(
      (sum, unit) => sum + (unit.lessons?.length ?? 0),
      0
    );

    // Course/unit tree exists but teacher hasn't added lessons yet
    if (courseData.units.length > 0 && totalLessons === 0) {
      navigate(`/course/${courseId}/curriculum`, { replace: true });
      return;
    }

    for (let unitIndex = 0; unitIndex < courseData.units.length; unitIndex++) {
      const unitAccess = isUnitAccessible(unitIndex, courseData.enrollmentDate, courseData.isOpenToAll);
      if (!unitAccess.accessible && unitAccess.message) {
        // Still show curriculum so they can see the structure / timers
        navigate(`/course/${courseId}/curriculum`, { replace: true });
        return;
      }
    }

    if (courseData.units.length > 0) {
      navigate(`/course/${courseId}/curriculum`, { replace: true });
      return;
    }

    setError('لا توجد وحدات متاحة حالياً');
    return;
  }

  // Check if all accessible lessons are completed
  const allCompleted = allLessons.every(({ lesson }) => 
    (lesson.type === 'Video' && lesson.isCompleted) || 
    (lesson.type === 'Quiz' && lesson.isQuizSubmitted) ||
    (lesson.type === 'Assignment' && lesson.isAssignmentSubmitted) ||
    (lesson.type === 'File' && lesson.isFileOpened)
  );

  // If all accessible lessons are completed, load the first accessible lesson
  if (allCompleted && allLessons.length > 0) {
    const { unitIndex, lessonIndex, lesson } = allLessons[0];
    setActiveUnit(unitIndex);
    setActiveLesson(lessonIndex);
    setSelectedLesson(lesson);
    await loadLessonDetails(lesson.id);
    navigate(`/course-player/${courseId}?unit=${unitIndex}&lesson=${lessonIndex}&lessonId=${lesson.id}`, { replace: true });
    return;
  }

  // Find the last completed lesson (video, quiz, assignment, or file) among accessible lessons
  let lastCompletedIdx = -1;
  for (let i = 0; i < allLessons.length; i++) {
    const { lesson } = allLessons[i];
    if ((lesson.type === 'Video' && lesson.isCompleted) || 
        (lesson.type === 'Quiz' && lesson.isQuizSubmitted) ||
        (lesson.type === 'Assignment' && lesson.isAssignmentSubmitted) ||
        (lesson.type === 'File' && lesson.isFileOpened)) {
      lastCompletedIdx = i;
    }
  }

  // Try to load the next lesson after the last completed
  const nextIndex = lastCompletedIdx + 1;
  if (nextIndex < allLessons.length) {
    const { unitIndex, lessonIndex, lesson } = allLessons[nextIndex];

    setActiveUnit(unitIndex);
    setActiveLesson(lessonIndex);
    setSelectedLesson(lesson);
    await loadLessonDetails(lesson.id);
    navigate(`/course-player/${courseId}?unit=${unitIndex}&lesson=${lessonIndex}&lessonId=${lesson.id}`, { replace: true });
    return;
  }

  // Fallback: first available not completed lesson among accessible lessons
  for (let i = 0; i < allLessons.length; i++) {
    const { unitIndex, lessonIndex, lesson } = allLessons[i];

    if (
      (lesson.type === 'Video' && !lesson.isCompleted) ||
      (lesson.type === 'Quiz' && !lesson.isQuizSubmitted) ||
      (lesson.type === 'Assignment' && !lesson.isAssignmentSubmitted) ||
      (lesson.type === 'File' && !lesson.isFileOpened)
    ) {
      setActiveUnit(unitIndex);
      setActiveLesson(lessonIndex);
      setSelectedLesson(lesson);
      await loadLessonDetails(lesson.id);
      navigate(`/course-player/${courseId}?unit=${unitIndex}&lesson=${lessonIndex}&lessonId=${lesson.id}`, { replace: true });
      return;
    }
  }

  // Check if the first lesson is a quiz that hasn't been submitted
  if (allLessons.length > 0) {
    const { unitIndex, lessonIndex, lesson } = allLessons[0];
    if (lesson.type === 'Quiz' && !lesson.isQuizSubmitted) {
      // Load the quiz but show a message that it needs to be completed first
      setActiveUnit(unitIndex);
      setActiveLesson(lessonIndex);
      setSelectedLesson(lesson);
      await loadLessonDetails(lesson.id);
      navigate(`/course-player/${courseId}?unit=${unitIndex}&lesson=${lessonIndex}&lessonId=${lesson.id}`, { replace: true });
      return;
    }
  }

  // Final fallback: if no incomplete lessons found, load the first accessible lesson
  if (allLessons.length > 0) {
    const { unitIndex, lessonIndex, lesson } = allLessons[0];
    setActiveUnit(unitIndex);
    setActiveLesson(lessonIndex);
    setSelectedLesson(lesson);
    await loadLessonDetails(lesson.id);
    navigate(`/course-player/${courseId}?unit=${unitIndex}&lesson=${lessonIndex}&lessonId=${lesson.id}`, { replace: true });
    return;
  }

  // Nothing available
  setError('لا توجد دروس متاحة حالياً');
};

  // Separate function to load lesson details
  const loadLessonDetails = async (lessonId: number) => {
    setLoading(true);
    setError(null);
    setPlaying(false);
    setLessonDetails(null);
    setSelectedFileIndex(null);

    try {
      console.log('Fetching lesson details for lesson ID:', lessonId);
      const response = await fetchLessonDetails(lessonId);
      console.log('Lesson details response:', response);

      if (response?.success) {
        setLessonDetails(response.data);
      } else {
        console.error('Failed to load lesson details:', response);
        setError('فشل في تحميل تفاصيل الدرس');
      }
    } catch (err) {
      console.error('Error loading lesson:', err);
      setError('حدث خطأ أثناء تحميل الدرس');
    } finally {
      setLoading(false);
    }
  };

  // Load course details and initialize first lesson
  useEffect(() => {
    const loadCourseDetails = async () => {
      if (courseId) {
        try {
          console.log('Loading course details for ID:', courseId);
          setLoading(true);
          const response = await fetchCourseDetails(courseId, true);
          console.log('Course details response:', response);

          if (response?.success) {
            setCourseDetails(response.data);
            
            // Check for URL parameters
            const unitParam = searchParams.get('unit');
            const lessonParam = searchParams.get('lesson');
            const lessonIdParam = searchParams.get('lessonId');

            if (lessonIdParam) {
              const lessonId = parseInt(lessonIdParam, 10);
              let found = false;
              for (let u = 0; u < response.data.units.length; u++) {
                const lessons = response.data.units[u].lessons || [];
                const l = lessons.findIndex((lesson: Lesson) => lesson.id === lessonId);
                if (l >= 0) {
                  const lesson = lessons[l];
                  setActiveUnit(u);
                  setActiveLesson(l);
                  setSelectedLesson(lesson);
                  await loadLessonDetails(lesson.id);
                  found = true;
                  break;
                }
              }
              if (!found) {
                await findAndLoadFirstLesson(response.data);
              }
            } else if (unitParam && lessonParam) {
              const unitIndex = parseInt(unitParam, 10);
              const lessonIndex = parseInt(lessonParam, 10);

              console.log('URL parameters found:', { unitIndex, lessonIndex });

              if (
                !isNaN(unitIndex) &&
                !isNaN(lessonIndex) &&
                unitIndex >= 0 &&
                unitIndex < response.data.units.length &&
                lessonIndex >= 0 &&
                response.data.units[unitIndex].lessons &&
                lessonIndex < response.data.units[unitIndex].lessons.length
              ) {
                console.log('Loading lesson from URL parameters');
                const lesson = response.data.units[unitIndex].lessons[lessonIndex];
                setActiveUnit(unitIndex);
                setActiveLesson(lessonIndex);
                setSelectedLesson(lesson);
                await loadLessonDetails(lesson.id);
              } else {
                console.log('Invalid URL parameters, finding first lesson');
                await findAndLoadFirstLesson(response.data);
              }
            } else {
              console.log('No URL parameters, finding first lesson');
              await findAndLoadFirstLesson(response.data);
            }
          } else {
            console.error('Failed to load course details:', response);
            setError(
              response?.message ||
                (response === null ? 'حدث خطأ أثناء تحميل الدورة' : 'Course details not found.')
            );
          }
        } catch (err) {
          console.error('Error loading course:', err);
          setError('حدث خطأ أثناء تحميل الدورة');
        } finally {
          setLoading(false);
        }
      } else {
        setError('missing course id');
        setLoading(false);
      }
    };

    loadCourseDetails();
  }, [courseId]);

  useEffect(() => {
    if (courseDetails) {
      const saved = localStorage.getItem(`course_${courseDetails.id}_completed`);
      if (saved) {
        setCompletedLessons(JSON.parse(saved));
      }
    }
  }, [courseDetails]);

  const handleLessonClick = async (unitIndex: number, lessonIndex: number) => {
    if (!courseDetails) {
      console.error('No course details available');
      return;
    }

    // Check if unit is accessible
    const unitAccess = isUnitAccessible(unitIndex, courseDetails.enrollmentDate, courseDetails.isOpenToAll);
    if (!unitAccess.accessible) {
      setError(unitAccess.message || 'هذه الوحدة غير متاحة حالياً');
      return;
    }

    const unit = courseDetails.units[unitIndex];
    if (!unit || !unit.lessons || lessonIndex >= unit.lessons.length) {
      console.error('Invalid unit or lesson index:', { unitIndex, lessonIndex, unit });
      setError('الدرس غير متوفر');
      return;
    }

    const lesson = unit.lessons[lessonIndex];
    console.log('Selected lesson:', lesson);

    setPlaying(false);
    setLessonDetails(null);
    setSelectedLesson(lesson);
    setActiveUnit(unitIndex);
    setActiveLesson(lessonIndex);
    setError(null);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('unit', String(unitIndex));
    nextParams.set('lesson', String(lessonIndex));
    nextParams.set('lessonId', String(lesson.id));
    setSearchParams(nextParams, { replace: true });

    await loadLessonDetails(lesson.id);
  };

  const handlePlayPause = () => {
    setPlaying(!playing);
  };

  const handleProgress = (state: { played: number; playedSeconds: number; loaded: number; loadedSeconds: number }) => {
    setProgress(state.played * 100);
    if (state.played > 0.9 && selectedLesson && !completedLessons.includes(selectedLesson.id.toString())) {
      setShowCompleteButton(true);
    }
  };

  const handleDuration = (duration: number) => {
    setDuration(duration);
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const bounds = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - bounds.left;
    const width = bounds.width;
    const percentage = (x / width) * 100;
    setProgress(percentage);
    if (playerRef.current) {
      playerRef.current.seekTo(percentage / 100);
    }
  };

  const handleVideoOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const videoUrl = lessonDetails && 'videoUrl' in lessonDetails ? lessonDetails.videoUrl : '';
    const isYt = Boolean(videoUrl && isYouTubeUrl(videoUrl));
    if (!isYt) {
      e.stopPropagation();
      handlePlayPause();
      showControlsTemporarily();
      return;
    }
    e.stopPropagation();
    if (youtubeOverlaySingleClickTimerRef.current !== null) {
      clearTimeout(youtubeOverlaySingleClickTimerRef.current);
      youtubeOverlaySingleClickTimerRef.current = null;
      return;
    }
    youtubeOverlaySingleClickTimerRef.current = window.setTimeout(() => {
      handlePlayPause();
      showControlsTemporarily();
      youtubeOverlaySingleClickTimerRef.current = null;
    }, YOUTUBE_OVERLAY_SINGLE_CLICK_MS);
  };

  const handleVideoOverlayDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const videoUrl = lessonDetails && 'videoUrl' in lessonDetails ? lessonDetails.videoUrl : '';
    if (!videoUrl || !isYouTubeUrl(videoUrl)) return;
    e.stopPropagation();
    if (youtubeOverlaySingleClickTimerRef.current !== null) {
      clearTimeout(youtubeOverlaySingleClickTimerRef.current);
      youtubeOverlaySingleClickTimerRef.current = null;
    }
    applyYoutubeDoubleClickSeek(e, playerRef, duration);
    showControlsTemporarily();
  };

  const formatTime = (seconds: number) => {
    const date = new Date(seconds * 1000);
    const hh = date.getUTCHours();
    const mm = date.getUTCMinutes();
    const ss = date.getUTCSeconds().toString().padStart(2, '0');
    if (hh) {
      return `${hh}:${mm.toString().padStart(2, '0')}:${ss}`;
    }
    return `${mm}:${ss}`;
  };

  const calculateProgress = () => {
    if (!courseDetails || !courseDetails.units) return 0;
    const totalLessons = courseDetails.units.reduce((total, unit) => total + unit.lessons.length, 0);
    const completed = completedLessons.length;
    return (completed / totalLessons) * 100;
  };

  const handleLessonComplete = () => {
    if (!courseDetails || !selectedLesson) return;
    
    setCompletedLessons(prev => {
      const lessonId = selectedLesson.id.toString();
      if (!prev.includes(lessonId)) {
        const newCompleted = [...prev, lessonId];
        localStorage.setItem(`course_${courseDetails.id}_completed`, JSON.stringify(newCompleted));
        return newCompleted;
      }
      return prev;
    });

    navigate(`/course/${courseId}/curriculum`);
  };

  // Type guard to check if lesson details is video lesson response
  const isVideoLessonResponse = (
    details:
      | VideoLessonResponse['data']
      | QuizLessonResponse['data']
      | FileLessonResponse['data']
      | AssignmentLessonResponse['data']
      | null
  ): details is VideoLessonResponse['data'] => {
    return !!details && typeof details === 'object' && 'videoUrl' in details;
  };

  // Type guard to check if lesson details is file lesson response
  const isFileLessonResponse = (
    details:
      | VideoLessonResponse['data']
      | QuizLessonResponse['data']
      | FileLessonResponse['data']
      | AssignmentLessonResponse['data']
      | null
  ): details is FileLessonResponse['data'] => {
    return (
      !!details &&
      typeof details === 'object' &&
      'files' in details &&
      Array.isArray((details as FileLessonResponse['data']).files)
    );
  };

  // Function to check if URL is a Bunny.net embed
  const isBunnyVideo = (url: string): boolean => {
    return url.includes('mediadelivery.net');
  };

  // Update the handleFullscreen function
  const handleFullscreen = async () => {
    if (!videoContainerRef.current) return;

    try {
      if (!document.fullscreenElement) {
        // Enter fullscreen
        if (videoContainerRef.current.requestFullscreen) {
          await videoContainerRef.current.requestFullscreen();
        } else if ((videoContainerRef.current as any).webkitRequestFullscreen) {
          await (videoContainerRef.current as any).webkitRequestFullscreen();
        }
        
        setIsFullscreen(true);

        // Check if it's a mobile device
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        
        if (isMobile) {
          // Force landscape orientation
          if ('orientation' in window.screen && 'lock' in window.screen.orientation) {
            try {
              await (window.screen.orientation as any).lock('landscape');
              setIsLandscape(true);
            } catch (error) {
              console.log('Failed to lock orientation:', error);
            }
          }
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        }
        
        setIsFullscreen(false);

        // Release orientation lock
        if ('orientation' in window.screen && 'unlock' in window.screen.orientation) {
          try {
            (window.screen.orientation as any).unlock();
            setIsLandscape(false);
          } catch (error) {
            console.log('Failed to unlock orientation:', error);
          }
        }
      }
    } catch (error) {
      console.log('Fullscreen error:', error);
    }
  };

  // Add fullscreen change event listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      if (!document.fullscreenElement) {
        setIsLandscape(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const currentVideoUrl =
    lessonDetails && 'videoUrl' in lessonDetails ? lessonDetails.videoUrl : '';
  const isCurrentYouTube = Boolean(currentVideoUrl && isYouTubeUrl(currentVideoUrl));

  // Handle volume change (YouTube embed syncs via props; ReactPlayer needs imperative API)
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    if (!isCurrentYouTube && playerRef.current) {
      (playerRef.current as any).player?.player?.setVolume?.(newVolume * 100);
    }
  };

  // Handle mute toggle
  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
    if (!isCurrentYouTube && playerRef.current) {
      if (!isMuted) {
        (playerRef.current as any).player?.player?.setVolume?.(0);
      } else {
        (playerRef.current as any).player?.player?.setVolume?.(volume * 100);
      }
    }
  };

  const handleSpeedChange = (speed: number) => {
    setPlaybackSpeed(speed);
    setShowSpeedMenu(false);
    if (!isCurrentYouTube && playerRef.current) {
      (playerRef.current as any).player?.player?.setPlaybackRate?.(speed);
    }
  };

  useEffect(() => {
    if (selectedLesson?.type === 'Quiz') {
      console.log('Selected quiz lesson with ID:', selectedLesson.id);
    }
  }, [selectedLesson]);

  // Course tree loaded with units but zero lessons → open curriculum (not a "broken course" screen)
  useEffect(() => {
    if (!courseId || !courseDetails || loading || isLoading) return;
    if (activeUnit !== null && activeLesson !== null) return;

    const totalLessons = courseDetails.units.reduce(
      (sum, unit) => sum + (unit.lessons?.length ?? 0),
      0
    );
    if (courseDetails.units.length > 0 && totalLessons === 0) {
      navigate(`/course/${courseId}/curriculum`, { replace: true });
    }
  }, [
    courseId,
    courseDetails,
    loading,
    isLoading,
    activeUnit,
    activeLesson,
    navigate,
  ]);

  const isBootstrapping =
    loading || isLoading || (!!courseId && !courseDetails && !error);

  if (isBootstrapping) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-green-50/40 via-white to-gold-50/30 px-4 font-cairo" dir="rtl">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-green-100 border-t-green-700" />
          <p className={`text-sm font-medium sm:text-base ${cardTheme.subtitle}`}>جاري تحميل الدرس...</p>
        </div>
      </div>
    );
  }

  // Course failed to load / missing id — only then treat as "course not found"
  if (!courseDetails) {
    const ui = mapCourseLoadError(
      !courseId ? 'missing course id' : error || 'Course details not found.'
    );
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-green-50/40 via-white to-gold-50/30 px-4 font-cairo" dir="rtl">
        <div className={`${cardTheme.shell} w-full max-w-md p-6 text-center sm:p-8`}>
          <h2 className={`mb-2 text-xl font-bold sm:text-2xl ${cardTheme.title}`}>{ui.title}</h2>
          <p className={`mb-6 text-sm sm:text-base ${cardTheme.subtitle}`}>{ui.message}</p>
          <button
            type="button"
            onClick={() => navigate('/student-portal')}
            className={`inline-flex min-h-11 w-full items-center justify-center rounded-xl px-6 py-3 text-sm font-bold sm:w-auto ${cardTheme.btnPrimary}`}
          >
            {ui.buttonLabel}
          </button>
        </div>
      </div>
    );
  }

  // Course loaded but no playable lesson yet (locked unit, empty, bad URL params)
  if (activeUnit === null || activeLesson === null) {
    const totalLessons = courseDetails.units.reduce(
      (sum, unit) => sum + (unit.lessons?.length ?? 0),
      0
    );

    // Redirect handled by effect — show brief loading while navigating to curriculum
    if (courseDetails.units.length > 0 && totalLessons === 0) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-green-50/40 via-white to-gold-50/30 px-4 font-cairo" dir="rtl">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-green-100 border-t-green-700" />
            <p className={`text-sm font-medium ${cardTheme.subtitle}`}>جاري فتح المنهج...</p>
          </div>
        </div>
      );
    }

    const ui = mapCourseLoadError(error || 'لا توجد دروس متاحة حالياً');
    const goCurriculum = () => navigate(`/course/${courseId}/curriculum`);
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-green-50/40 via-white to-gold-50/30 px-4 font-cairo" dir="rtl">
        <div className={`${cardTheme.shell} w-full max-w-md p-6 text-center sm:p-8`}>
          <h2 className={`mb-2 text-xl font-bold sm:text-2xl ${cardTheme.title}`}>{ui.title}</h2>
          <p className={`mb-6 text-sm sm:text-base ${cardTheme.subtitle}`}>{ui.message}</p>
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={goCurriculum}
              className={`inline-flex min-h-11 items-center justify-center rounded-xl px-6 py-3 text-sm font-bold ${cardTheme.btnPrimary}`}
            >
              ارجع للمنهج
            </button>
            <button
              type="button"
              onClick={() => navigate('/student-portal')}
              className={`inline-flex min-h-11 items-center justify-center rounded-xl px-6 py-3 text-sm font-bold ${cardTheme.btnOutline}`}
            >
              روح لكورساتي
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentUnit = courseDetails.units[activeUnit];
  const currentLesson = currentUnit?.lessons[activeLesson];

  if (!currentUnit || !currentLesson) {
    const ui = mapCourseLoadError('الدرس غير متوفر');
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-green-50/40 via-white to-gold-50/30 px-4 font-cairo" dir="rtl">
        <div className={`${cardTheme.shell} w-full max-w-md p-6 text-center sm:p-8`}>
          <h2 className={`mb-2 text-xl font-bold sm:text-2xl ${cardTheme.title}`}>{ui.title}</h2>
          <p className={`mb-6 text-sm sm:text-base ${cardTheme.subtitle}`}>{ui.message}</p>
          <button
            type="button"
            onClick={() => navigate(`/course/${courseId}/curriculum`)}
            className={`inline-flex min-h-11 w-full items-center justify-center rounded-xl px-6 py-3 text-sm font-bold sm:w-auto ${cardTheme.btnPrimary}`}
          >
            ارجع للمنهج
          </button>
        </div>
      </div>
    );
  }

  const totalLessons = courseDetails.units.reduce((total, unit) => total + unit.lessons.length, 0);
  const currentLessonNumber = courseDetails.units.slice(0, activeUnit).reduce((total, unit) => total + unit.lessons.length, 0) + activeLesson + 1;

  const markLessonAsCompleted = async (lessonId: number) => {
    // Update local state without API call
    if (selectedLesson && selectedLesson.id === lessonId && selectedLesson.type === 'Video') {
      setSelectedLesson({ ...selectedLesson, isCompleted: true });
    }
    
    // Add to completed lessons locally
    setCompletedLessons(prev => {
      const lessonIdStr = lessonId.toString();
      if (!prev.includes(lessonIdStr)) {
        const newCompleted = [...prev, lessonIdStr];
        localStorage.setItem(`course_${courseId}_completed`, JSON.stringify(newCompleted));
        return newCompleted;
      }
      return prev;
    });
    
    // Show success message
    setShowBravo(true);
    setTimeout(() => {
      setShowBravo(false);
    }, 2000);
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-paper">
      {/* Top Navigation Bar */}
      <div className="bg-white shadow-sm border-b border-slate-200 fixed top-0 left-0 right-0 z-50">
        <div className="h-16 flex items-center justify-between gap-2 px-3 sm:px-4 lg:px-6 max-w-[1920px] mx-auto min-w-0">
          <div className="flex items-center gap-2 shrink-0 min-w-0">
            {/* Back to Curriculum Button */}
            <button 
              onClick={() => navigate(`/course/${courseId}/curriculum`)}
              className="p-2 rounded-xl hover:bg-slate-100 transition-colors flex items-center gap-1.5 text-slate-700 min-h-11"
              aria-label="العودة للمحتوى"
            >
              <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
              <span className="hidden sm:inline text-sm">العودة إلى المنهج</span>
            </button>
          </div>

          {/* Course Title and Progress */}
          <div className="flex-1 min-w-0 mx-2 hidden sm:block">
            <h1 className="text-lg font-semibold text-slate-900 truncate">
              {courseDetails?.courseName}
            </h1>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {`${currentLessonNumber} / ${totalLessons}`}
              </span>
              <div className="h-4 w-px bg-slate-200"></div>
              <span className="text-blue-600 font-medium">{calculateProgress().toFixed(0)}% Complete</span>
            </div>
          </div>

          {/* Mobile Course Title */}
          <div className="flex-1 min-w-0 mx-1 sm:hidden">
            <h1 className="text-sm font-semibold text-slate-900 truncate">
              {courseDetails?.courseName}
            </h1>
          </div>

          {/* Progress Bar */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-100">
            <div 
              className="h-full bg-blue-500 transition-all duration-300 ease-in-out"
              style={{ width: `${calculateProgress()}%` }}
            />
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="pt-16 overflow-y-auto overflow-x-hidden" style={{ height: '100vh', minHeight: '100vh' }}>
        {/* Main Content - Full width */}
        <main className="w-full max-w-full min-w-0 h-full overflow-x-hidden">
          <div className="w-full max-w-full min-w-0 mx-auto px-0 py-0 h-full">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : error &&
              !(selectedLesson?.type === 'Quiz' && selectedLesson.isQuizSubmitted === true) ? (
              <div className="px-4 py-8 text-center font-cairo" dir="rtl">
                <div className="mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-bold text-green-900">{error}</h3>
                <p className="mb-4 text-sm text-gray-600">جرّب تاني، ولو المشكلة فضلت كلّم الإشراف.</p>
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    navigate(`/course/${courseId}/curriculum`);
                  }}
                  className={`inline-flex min-h-11 items-center justify-center rounded-xl px-5 py-2.5 text-sm font-bold ${cardTheme.btnPrimary}`}
                >
                  ارجع للمنهج
                </button>
              </div>
            ) : selectedLesson &&
              (lessonDetails ||
                (selectedLesson.type === 'Quiz' && selectedLesson.isQuizSubmitted === true)) ? (
              <div className="h-full w-full max-w-full min-w-0 relative overflow-x-hidden">
                {/* Unit Access Timer - video lessons only */}
                {courseDetails &&
                  activeUnit !== null &&
                  !courseDetails.isOpenToAll &&
                  selectedLesson.type === 'Video' && (
                  <UnitAccessTimer 
                    unitIndex={activeUnit}
                    enrollmentDate={courseDetails.enrollmentDate}
                    totalUnits={courseDetails.units.length}
                  />
                )}
                
                {selectedLesson.type === 'Video' &&
                  isVideoLessonResponse(lessonDetails) &&
                  !isQuizContent(lessonDetails) && (
                  <>
                    <div 
                      ref={videoContainerRef}
                      className={`video-container relative overflow-hidden w-full h-full bg-slate-900
                        ${isFullscreen ? 'fixed inset-0 z-50 bg-black' : ''}`}
                      onMouseMove={showControlsTemporarily}
                      onClick={showControlsTemporarily}
                      style={{ width: '100%', height: '100%' }}
                    >
                      <div className="h-full w-full bg-slate-900 relative" style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
                        {/* Floating Watermark (YouTube: inside Plyr so it survives fullscreen) */}
                        {userPhoneNumber && !isYouTubeUrl(lessonDetails.videoUrl) && (
                          <VideoWatermark 
                            phoneNumber={userPhoneNumber} 
                            isVisible={true}
                            opacity={0.2}
                            speed={300}
                          />
                        )}
                        
                        {isBunnyVideo(lessonDetails.videoUrl) ? (
                          <iframe
                            src={`${lessonDetails.videoUrl}${lessonDetails.videoUrl.includes('?') ? '&' : '?'}autoplay=false&loop=false&muted=false&preload=true&responsive=false`}
                            className="w-full h-full border-0"
                            style={{
                              position: 'absolute',
                              top: '-50%',
                              left: '-50%',
                              width: '200%',
                              height: '200%',
                            }}
                            allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
                            allowFullScreen
                            title="Bunny Video Player"
                          />
                        ) : isYouTubeUrl(lessonDetails.videoUrl) ? (
                          <PlyrYouTubePlayer
                            key={`plyr-${lessonDetails.videoUrl}`}
                            ref={playerRef as React.Ref<PlyrYouTubePlayerHandle>}
                            url={lessonDetails.videoUrl}
                            title={selectedLesson.lessonName || 'YouTube video player'}
                            phoneNumber={userPhoneNumber}
                            watermarkOpacity={0.2}
                            watermarkSpeed={300}
                            onProgress={handleProgress}
                            onDuration={handleDuration}
                            onPlay={() => setPlaying(true)}
                            onPause={() => setPlaying(false)}
                          />
                        ) : (
                          <>
                            <ReactPlayer
                              key={`player-${selectedQuality}`}
                              ref={playerRef as React.Ref<ReactPlayer>}
                              url={lessonDetails.videoUrl}
                              width="100%"
                              height="100%"
                              playing={playing}
                              controls={false}
                              playbackRate={playbackSpeed}
                              onProgress={handleProgress}
                              onDuration={handleDuration}
                            />
                            
                            {/* Custom Video Controls (non-YouTube only — YouTube uses Plyr UI) */}
                            <div 
                              className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-opacity duration-300 ${
                                controlsVisible ? 'opacity-100' : 'opacity-0'
                              }`}
                            >
                              <div 
                                className="absolute inset-0 z-10 cursor-pointer"
                                onClick={handleVideoOverlayClick}
                                onDoubleClick={handleVideoOverlayDoubleClick}
                              />
                              
                              <div className={`absolute bottom-0 left-0 right-0 p-4 z-20 transition-opacity duration-300 ${
                                controlsVisible ? 'opacity-100' : 'opacity-0'
                              }`}>
                                <div 
                                  className="relative w-full h-1.5 bg-white/20 rounded-full mb-4 cursor-pointer group"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSeek(e);
                                    showControlsTemporarily();
                                  }}
                                >
                                  <div 
                                    className="absolute left-0 top-0 h-full bg-orange-500 rounded-full transition-all duration-150 group-hover:bg-orange-400"
                                    style={{ width: `${progress}%` }}
                                  />
                                  {/* Add hover effect preview dot */}
                                  <div 
                                    className="absolute top-1/2 -translate-y-1/2 h-3 w-3 bg-orange-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                    style={{ left: `${progress}%`, transform: `translate(-50%, -50%)` }}
                                  />
                                </div>
                              
                                {/* Controls Bar */}
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-4">
                                    <button
                                      onClick={handlePlayPause}
                                      className="text-white hover:text-orange-400 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-black rounded-lg"
                                    >
                                      {playing ? (
                                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                      ) : (
                                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                      )}
                                    </button>

                                    <div className="flex items-center gap-2 group relative">
                                      <button
                                        onClick={handleMuteToggle}
                                        className="text-white hover:text-orange-400 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-black rounded-lg"
                                      >
                                        {isMuted || volume === 0 ? (
                                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                                          </svg>
                                        ) : (
                                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 11.646a2 2 0 010 2.828m-4.95-7.778l4.95 4.95m0 0l4.95 4.95" />
                                          </svg>
                                        )}
                                      </button>
                                      <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.1"
                                        value={volume}
                                        onChange={handleVolumeChange}
                                        className="w-0 group-hover:w-20 transition-all duration-200 opacity-0 group-hover:opacity-100 accent-blue-500"
                                      />
                                    </div>

                                    <span className="text-white text-sm font-medium">
                                      {formatTime(duration * progress / 100)} / {formatTime(duration)}
                                    </span>
                                  </div>
                                  
                                  <div className="flex items-center gap-4">
                                    <div className="relative group">
                                      <button
                                        onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                                        className="text-white hover:text-orange-400 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-black rounded-lg flex items-center gap-2 px-3 py-1.5 bg-black/20"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                        <span className="text-sm font-medium">{playbackSpeed}x</span>
                                      </button>

                                      {showSpeedMenu && (
                                        <div 
                                          className="absolute bottom-full right-0 mb-2 bg-black/95 rounded-xl py-2 min-w-[100px] shadow-lg backdrop-blur-sm border border-white/10 transform origin-bottom-right z-50"
                                        >
                                          {playbackSpeeds.map((speed) => (
                                            <button
                                              key={speed}
                                              onClick={() => handleSpeedChange(speed)}
                                              className={`w-full px-4 py-1.5 text-left text-sm hover:bg-white/10 transition-colors flex items-center justify-between ${
                                                playbackSpeed === speed ? 'text-blue-400 bg-blue-500/10' : 'text-white'
                                              }`}
                                            >
                                              <span>{speed}x</span>
                                              {playbackSpeed === speed && (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                              )}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>

                                    {/* Quality Selector */}
                                    <QualitySelector
                                      selectedQuality={selectedQuality}
                                      onQualityChange={handleQualityChange}
                                      isYouTube={isYouTubeUrl(lessonDetails.videoUrl)}
                                      connectionSpeed={connectionSpeed}
                                    />

                                    <button
                                      onClick={handleFullscreen}
                                      className="text-white hover:text-orange-400 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-black rounded-lg"
                                    >
                                      {isFullscreen ? (
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9h6v6H9z" />
                                        </svg>
                                      ) : (
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                        </svg>
                                      )}
                                    </button>

                                  {showCompleteButton && !completedLessons.includes(selectedLesson.id.toString()) && (
                                    <button
                                      onClick={handleLessonComplete}
                                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-black"
                                    >
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                        إكمال الدرس
                                    </button>
                                  )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {selectedLesson.type === 'Video' && !selectedLesson.isCompleted && (
                      <div className="mt-4 flex justify-center">
                        <button
                          onClick={() => markLessonAsCompleted(selectedLesson.id)}
                          className="px-6 py-3 bg-green-600 text-white rounded-lg font-bold shadow hover:bg-green-700 transition"
                        >
                          أتفرجت على المحاضرة
                        </button>
                      </div>
                    )}

                    {/* Make attachments section responsive */}
                    {lessonDetails.attachmentUrl && lessonDetails.attachmentTitle && (
                      <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500/10 to-blue-600/20 flex items-center justify-center">
                              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-slate-900">Course Materials</h3>
                              <div className="flex items-center gap-2">
                                <span className="text-slate-600 text-sm">{lessonDetails.attachmentTitle}</span>
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {lessonDetails.attachmentTitle.split('.').pop()?.toUpperCase()}
                                </span>
                              </div>
                            </div>
                          </div>
                          <a
                            href={lessonDetails.attachmentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all duration-200"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download
                          </a>
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {selectedLesson.type === 'Quiz' &&
                  (selectedLesson.isQuizSubmitted === true || isQuizContent(lessonDetails)) && (
                  <div className="min-h-full w-full max-w-full min-w-0 overflow-x-hidden bg-paper px-3 py-4 sm:px-6 sm:py-8">
                    <div className="mx-auto w-full max-w-2xl min-w-0">
                      {selectedLesson.lessonName && (
                        <div className="mb-4 min-w-0 sm:mb-5">
                          <h2 className="break-words text-lg font-bold text-green-800 sm:text-xl font-cairo">
                            {selectedLesson.lessonName}
                          </h2>
                          <p className="mt-1 text-sm text-gray-600">
                            {selectedLesson.isQuizSubmitted
                              ? 'تم تسليم هذا الاختبار مسبقاً'
                              : 'أجب عن الأسئلة ثم أرسل النتيجة'}
                          </p>
                        </div>
                      )}
                      <QuizContent
                        questions={isQuizContent(lessonDetails) ? lessonDetails.questions : []}
                        onComplete={() => {
                          if (selectedLesson.type === 'Quiz') {
                            setSelectedLesson({
                              ...selectedLesson,
                              isQuizSubmitted: true,
                            });
                            setCourseDetails((prev) => {
                              if (!prev || activeUnit === null || activeLesson === null) return prev;
                              const units = prev.units.map((unit, ui) => {
                                if (ui !== activeUnit) return unit;
                                return {
                                  ...unit,
                                  lessons: unit.lessons.map((lesson, li) => {
                                    if (li !== activeLesson || lesson.type !== 'Quiz') return lesson;
                                    return { ...lesson, isQuizSubmitted: true };
                                  }),
                                };
                              });
                              return { ...prev, units };
                            });
                          }
                          handleLessonComplete();
                        }}
                        lessonId={selectedLesson.id}
                        courseId={courseId}
                        alreadySubmitted={selectedLesson.isQuizSubmitted === true}
                      />
                    </div>
                  </div>
                )}

                {selectedLesson.type === 'Assignment' && isAssignmentContent(lessonDetails) && (
                  <div className="min-h-full w-full max-w-full min-w-0 overflow-x-hidden bg-paper px-3 py-4 sm:px-6 sm:py-8">
                    <div className="mx-auto w-full max-w-2xl min-w-0">
                      <AssignmentContent
                        assignment={lessonDetails}
                        onComplete={() => {
                          if (selectedLesson.type === 'Assignment') {
                            setSelectedLesson({
                              ...selectedLesson,
                              isAssignmentSubmitted: true,
                            });
                            setCourseDetails((prev) => {
                              if (!prev || activeUnit === null || activeLesson === null) return prev;
                              const units = prev.units.map((unit, ui) => {
                                if (ui !== activeUnit) return unit;
                                return {
                                  ...unit,
                                  lessons: unit.lessons.map((lesson, li) => {
                                    if (li !== activeLesson || lesson.type !== 'Assignment') return lesson;
                                    return { ...lesson, isAssignmentSubmitted: true };
                                  }),
                                };
                              });
                              return { ...prev, units };
                            });
                          }
                          setCompletedLessons((prev) => {
                            const lessonId = selectedLesson.id.toString();
                            if (prev.includes(lessonId) || !courseDetails) return prev;
                            const next = [...prev, lessonId];
                            localStorage.setItem(
                              `course_${courseDetails.id}_completed`,
                              JSON.stringify(next)
                            );
                            return next;
                          });
                          navigate(`/course/${courseId}/curriculum`);
                        }}
                      />
                    </div>
                  </div>
                )}
                
                {selectedLesson.type === 'File' && isFileLessonResponse(lessonDetails) && (
                  <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-lg">
                    <div className="mb-6">
                      <h2 className="text-2xl font-bold text-slate-900 mb-2">{selectedLesson.lessonName}</h2>
                      {selectedLesson.titel && selectedLesson.titel !== 'null' && (
                        <p className="text-slate-600">{selectedLesson.titel}</p>
                      )}
                    </div>
                    
                    {lessonDetails.files && lessonDetails.files.length > 0 ? (
                      <>
                        {/* File List */}
                        {selectedFileIndex === null ? (
                          <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-slate-900 mb-4">الملفات</h3>
                            <div className="space-y-3">
                              {lessonDetails.files.map((file, index) => {
                                const isPdf = file.fileUrl.toLowerCase().includes('.pdf') || 
                                             file.fileName.toLowerCase().endsWith('.pdf') ||
                                             file.fileUrl.includes('drive.google.com') && file.fileUrl.includes('/file/d/');
                                
                                return (
                                  <button
                                    key={index}
                                    onClick={() => {
                                      if (isPdf) {
                                        setSelectedFileIndex(index);
                                      } else {
                                        window.open(file.fileUrl, '_blank', 'noopener,noreferrer');
                                      }
                                    }}
                                    className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors border border-slate-200 group text-right"
                                  >
                                    <div className="flex items-center gap-4 flex-1">
                                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                        isPdf 
                                          ? 'bg-gradient-to-br from-red-500/10 to-red-600/20' 
                                          : 'bg-gradient-to-br from-purple-500/10 to-purple-600/20'
                                      }`}>
                                        {isPdf ? (
                                          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                          </svg>
                                        ) : (
                                          <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                          </svg>
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <h4 className="font-medium text-slate-900 truncate">{file.fileName}</h4>
                                        <p className="text-sm text-slate-500 truncate">{file.fileUrl}</p>
                                      </div>
                                    </div>
                                    {isPdf ? (
                                      <svg className="w-5 h-5 text-slate-400 group-hover:text-red-600 transition-colors flex-shrink-0 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                      </svg>
                                    ) : (
                                      <svg className="w-5 h-5 text-slate-400 group-hover:text-purple-600 transition-colors flex-shrink-0 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                      </svg>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          /* PDF Viewer */
                          <div className="space-y-4">
                            <div className="flex items-center justify-between mb-4">
                              <button
                                onClick={() => setSelectedFileIndex(null)}
                                className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                </svg>
                                <span>العودة إلى قائمة الملفات</span>
                              </button>
                              <h3 className="text-lg font-semibold text-slate-900">
                                {lessonDetails.files[selectedFileIndex]?.fileName}
                              </h3>
                            </div>
                            
                            <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50">
                              <div className="w-full" style={{ height: '80vh', minHeight: '600px' }}>
                                {(() => {
                                  const selectedFile = lessonDetails.files[selectedFileIndex];
                                  if (!selectedFile) return null;
                                  
                                  // Convert Google Drive share link to embeddable format
                                  let pdfUrl = selectedFile.fileUrl;
                                  if (pdfUrl.includes('drive.google.com') && pdfUrl.includes('/file/d/')) {
                                    const fileIdMatch = pdfUrl.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
                                    if (fileIdMatch) {
                                      pdfUrl = `https://drive.google.com/file/d/${fileIdMatch[1]}/preview`;
                                    }
                                  }
                                  
                                  return (
                                    <iframe
                                      src={pdfUrl}
                                      className="w-full h-full border-0"
                                      title={selectedFile.fileName}
                                      allow="fullscreen"
                                    />
                                  );
                                })()}
                              </div>
                            </div>
                            
                            {/* Download button */}
                            <div className="flex justify-center">
                              <a
                                href={lessonDetails.files[selectedFileIndex]?.fileUrl}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                <span>تحميل الملف</span>
                              </a>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <svg className="w-12 h-12 mx-auto mb-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <p>لا توجد ملفات متاحة</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </main>
      </div>

      {showBravo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-lg px-10 py-8 text-3xl font-bold text-green-600 animate-bounce">
            برافوووو 🎉
          </div>
        </div>
      )}

      {/* Quality Change Notification */}
      {qualityChangeNotification && (
        <div className="fixed top-20 right-4 z-50 max-w-sm">
          <div className="bg-orange-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-slide-in-right">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-sm font-medium">{qualityChangeNotification}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseViewer; 